import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Logo } from '@/components/Logo';
import {
  Play,
  Plus,
  TrendingUp,
  Award,
  Sparkles,
  Trophy,
  GraduationCap,
  MessageSquare,
  BarChart3,
  Briefcase,
  Users,
  UserPlus,
  Search,
  Moon,
  Bell,
  ChevronRight,
  FileText,
  Eye,
} from 'lucide-react';

const AppShowcase = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.2 });

  const navPrimary = [
    { icon: Play, label: 'Practice', active: true },
    { icon: Plus, label: 'Create', chevron: true },
    { icon: Trophy, label: 'Competitions', beta: true },
    { icon: GraduationCap, label: 'Academy', beta: true },
  ];

  const navSecondary = [
    { icon: BarChart3, label: 'Progress', chevron: true },
    { icon: Briefcase, label: 'Career' },
  ];

  const navTertiary = [
    { icon: Users, label: 'Groups' },
    { icon: TrendingUp, label: 'Instructor Dashboard' },
    { icon: UserPlus, label: 'Recruit' },
  ];

  const filters = [
    { label: 'Official', count: '9', active: true },
    { label: 'UACU', count: '0' },
    { label: 'Custom', count: '120' },
    { label: 'Comedy', count: '2' },
  ];

  const difficulties = [
    { label: 'Beginner', count: '3' },
    { label: 'Standard', count: '1' },
    { label: 'Advanced', count: '1' },
    { label: 'Competition', count: '4', active: true },
  ];

  const scenarios = [
    {
      title: 'NCSC 2026 — Gartner',
      tags: [{ label: 'NCSC 2026', brand: true }, { label: '4 Rounds' }],
      sub: 'Gartner • Various Sales Leaders',
      desc: 'National Collegiate Sales Competition 2026. Sell Gartner for Sales Leaders across 4 rounds with different prospects.',
    },
    {
      title: 'National Tech-Sales Competition',
      tags: [{ label: 'MTSU', brand: true }, { label: '3 Rounds' }],
      sub: 'Oracle / Cumberland Inc. • Various Stakeholders',
      desc: 'Sell NetSuite Advanced Inventory to Cumberland Inc. across 3 rounds — from discovery to close.',
    },
    {
      title: 'TEKsystems — Terazo Inc.',
      tags: [{ label: 'EKU Tech Sales' }, { label: 'IT Staffing' }],
      sub: 'TEKsystems • Technical Director, Healthcare Division',
      desc: "Sell TEKsystems' IT staffing solutions to Terazo Inc. to support their VA healthcare IVR project.",
    },
    {
      title: 'MC Bliss Tray — Heritage Vineyards',
      tags: [{ label: 'UTISC 2026', brand: true }, { label: 'Packaging' }],
      sub: 'International Paper • Packaging Operations Supervisor',
      desc: 'Sell the MC Bliss Tray packaging solution to Heritage Vineyards to address stacking failures.',
    },
  ];

  return (
    <section ref={sectionRef} className="py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 relative">
        {/* Section Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-flex items-center gap-2 mb-4 rounded-full border border-subtle bg-card/60 px-3 py-1.5 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              See it in action
            </span>
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
            One dashboard for every rep
          </h2>
          <p className="text-muted-foreground text-lg">
            Official competition cases, custom scenarios, and rubric-grade feedback — all in one place.
          </p>
        </div>

        {/* Dashboard mockup */}
        <div
          className={`relative transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
          }`}
        >
          <div className="absolute inset-x-8 bottom-0 h-24 bg-primary/10 blur-3xl" />

          <div className="relative rounded-2xl overflow-hidden border border-subtle bg-card">
            <div className="flex min-h-[480px]">
              {/* Sidebar */}
              <aside className="hidden md:flex w-[210px] shrink-0 flex-col border-r border-subtle bg-background/40">
                <div className="h-14 flex items-center px-4 border-b border-transparent">
                  <Logo size="sm" />
                </div>
                <nav className="px-2 pb-4 space-y-0.5">
                  {navPrimary.map((item) => (
                    <NavRow key={item.label} {...item} />
                  ))}
                  <div className="my-3 mx-2 border-t border-subtle" />
                  {navSecondary.map((item) => (
                    <NavRow key={item.label} {...item} />
                  ))}
                  <div className="my-3 mx-2 border-t border-subtle" />
                  {navTertiary.map((item) => (
                    <NavRow key={item.label} {...item} />
                  ))}
                </nav>
              </aside>

              {/* Content */}
              <div className="flex-1 min-w-0 flex flex-col">
                {/* Top bar */}
                <div className="h-14 flex items-center gap-3 px-4 sm:px-5 border-b border-subtle">
                  <div className="flex-1 max-w-sm flex items-center gap-2 rounded-lg border border-subtle bg-background/60 px-3 py-1.5">
                    <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">Search dashboard...</span>
                    <span className="ml-auto text-[10px] text-muted-foreground/70 font-mono">⌘K</span>
                  </div>
                  <div className="hidden lg:flex items-center gap-2">
                    {['Student View', 'Professor View'].map((v) => (
                      <span
                        key={v}
                        className="inline-flex items-center gap-1.5 rounded-full border border-subtle px-2.5 py-1 text-[11px] text-muted-foreground"
                      >
                        <Eye className="w-3 h-3" />
                        {v}
                      </span>
                    ))}
                  </div>
                  <Moon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <Bell className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="hidden sm:block text-right leading-tight">
                    <p className="text-xs font-medium text-foreground">Christian Carroll</p>
                    <p className="text-[10px] text-muted-foreground">UACU</p>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/40 to-accent/40 border border-subtle shrink-0" />
                </div>

                {/* Body */}
                <div className="p-4 sm:p-6 flex-1">
                  <h3 className="font-display text-xl sm:text-3xl text-foreground">
                    Good morning, Christian
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    You're on a 4-day streak — keep it up.
                  </p>

                  <div className="mt-5 pt-5 border-t border-subtle flex flex-wrap items-center gap-3">
                    <h4 className="font-display text-base sm:text-xl text-foreground flex items-center gap-2">
                      <Play className="w-4 h-4 text-primary" />
                      Practice Scenarios
                    </h4>
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1 text-[11px] text-muted-foreground">
                      <FileText className="w-3 h-3" /> Analyze
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-subtle px-2.5 py-1 text-[11px] text-muted-foreground">
                      <Plus className="w-3 h-3" /> Quick Create
                    </span>
                  </div>

                  {/* Segmented filters */}
                  <div className="mt-4 grid grid-cols-4 gap-1 rounded-xl border border-subtle bg-background/40 p-1">
                    {filters.map((f) => (
                      <div
                        key={f.label}
                        className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] sm:text-xs ${
                          f.active
                            ? 'bg-card border border-subtle text-foreground font-medium'
                            : 'text-muted-foreground'
                        }`}
                      >
                        <span className="truncate">{f.label}</span>
                        <span className="text-[10px] tabular-nums text-muted-foreground/70">{f.count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Difficulty underline tabs */}
                  <div className="mt-3 flex items-center gap-5 border-b border-subtle">
                    {difficulties.map((d) => (
                      <div
                        key={d.label}
                        className={`relative pb-2 text-[11px] sm:text-xs flex items-center gap-1.5 ${
                          d.active ? 'text-foreground font-medium' : 'text-muted-foreground'
                        }`}
                      >
                        {d.label}
                        <span className="text-[10px] tabular-nums text-muted-foreground/70">{d.count}</span>
                        {d.active && (
                          <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Scenario cards */}
                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {scenarios.map((s, i) => (
                      <div
                        key={s.title}
                        className="rounded-xl border border-subtle bg-background/40 p-4 transition-colors hover:border-primary/30"
                        style={{
                          transitionDelay: `${i * 80}ms`,
                          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                          opacity: isVisible ? 1 : 0,
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-display text-sm sm:text-base text-foreground leading-snug">
                                {s.title}
                              </p>
                              {s.tags.map((t) => (
                                <span
                                  key={t.label}
                                  className={`rounded-md px-1.5 py-0.5 text-[10px] ${
                                    t.brand
                                      ? 'bg-primary text-primary-foreground'
                                      : 'border border-subtle text-muted-foreground'
                                  }`}
                                >
                                  {t.label}
                                </span>
                              ))}
                            </div>
                            <p className="mt-1.5 text-[11px] sm:text-xs text-muted-foreground">{s.sub}</p>
                            <p className="mt-2 text-[11px] sm:text-xs text-muted-foreground/70 leading-relaxed line-clamp-2">
                              {s.desc}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground/60 shrink-0 mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {[
            { icon: TrendingUp, title: 'Real-time Analytics', desc: 'Track improvement across every rubric category' },
            { icon: Award, title: 'Achievement System', desc: 'Earn badges and maintain practice streaks' },
            { icon: MessageSquare, title: 'AI Coaching', desc: 'Personalized tips based on your performance' },
          ].map((item, index) => (
            <div
              key={item.title}
              className={`rounded-xl border border-subtle bg-card p-4 text-center transition-all duration-700 hover:border-primary/30 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${600 + index * 150}ms` }}
            >
              <div className="w-11 h-11 mx-auto mb-3 rounded-xl bg-primary/10 flex items-center justify-center">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

type NavRowProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  beta?: boolean;
  chevron?: boolean;
};

const NavRow = ({ icon: Icon, label, active, beta, chevron }: NavRowProps) => (
  <div
    className={`relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs ${
      active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground'
    }`}
  >
    {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
    <Icon className="w-3.5 h-3.5 shrink-0" />
    <span className="truncate">{label}</span>
    {beta && (
      <span className="rounded-md bg-primary/15 px-1 py-0.5 text-[9px] font-medium text-primary">BETA</span>
    )}
    {chevron && <ChevronRight className="w-3 h-3 ml-auto text-muted-foreground/60" />}
  </div>
);

export default AppShowcase;
